import { tileAt, type MapDef } from '../world/map';
import { T, BUILDING_WALLS, ROOFS } from '../world/tiles';
import { fbm, hash2, hex, mix, noise2, scale, smoothstep, type RGB } from './noise';
import { colorAt2, PLACEHOLDER2 } from './groundPixels2';
import { PLACEHOLDER3 } from './groundPixels3';

/** Detail pixels per logical pixel for the ground layer (2 = twice the resolution of the sprites' grid). */
export const D = 2;
/** Chunk size in logical pixels. */
export const CHUNK = 128;

const NATURAL = new Set(['g', 'G', 'F', 'p', 'S']);
const C = {
  grassDark: hex('#4b8f40'),
  grassLight: hex('#82c25e'),
  grassSun: hex('#b4dc7c'),
  meadow: hex('#90c862'),
  fieldA: hex('#5aa64e'),
  fieldB: hex('#6db85c'),
  pathA: hex('#c9ae7c'),
  pathB: hex('#dfcb9f'),
  pathMoss: hex('#98ad62'),
  pebbleD: hex('#a8916c'),
  pebbleL: hex('#efe4c8'),
  sandA: hex('#e6cd92'),
  sandB: hex('#f5e5b6'),
  trackA: hex('#b5523a'),
  trackB: hex('#cd6c4a'),
  road: hex('#47484e'),
  roadSpeck: hex('#6c6d73'),
  crack: hex('#303136'),
  stoneA: hex('#c2bbae'),
  stoneB: hex('#d9d3c7'),
  grout: hex('#8e8678'),
  moss: hex('#7a8f56'),
  shallow: hex('#7fd0da'),
  deep: hex('#2c73ad'),
  foam: hex('#eef9f6'),
  wetSand: hex('#b9a77a'),
  woods: ['#995f34', '#a86e3e', '#b47c48', '#90562d', '#a3693a'].map(hex),
  tileCream: hex('#e6e0cc'),
  tileSage: hex('#cfd8c2'),
  tileGrout: hex('#b3aa94'),
  kitchenW: hex('#f4f1e8'),
  kitchenB: hex('#a6cadc'),
  bathA: hex('#eef7f9'),
  bathB: hex('#d4e8ee'),
  carpetRed: [hex('#ac4f3b'), hex('#c4664c'), hex('#e6b866')] as RGB[],
  carpetGreen: [hex('#3c765a'), hex('#4b8c6b'), hex('#dcc47c')] as RGB[],
};

function baseMaterial(map: MapDef, tx: number, ty: number): string {
  const ch = tileAt(map, tx, ty);
  switch (ch) {
    case 'h':
    case 'f':
      return 'g';
    case 'l':
    case '=':
      return 'r';
    case 'd': {
      const below = tileAt(map, tx, ty + 1), above = tileAt(map, tx, ty - 1);
      return '.,:;kbzmu'.includes(below) ? below : '.,:;kbzmu'.includes(above) ? above : ',';
    }
    case 'D':
      return map.indoor ? ',' : 'B';
    default:
      return ch;
  }
}

/** Material at a logical point, with organic (noise-warped) borders between natural ground types. */
const WARPED = new Set([...NATURAL, '~', 'o', 'Z']);
let warpX = 0, warpY = 0;

function materialAt(map: MapDef, x: number, y: number): string {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const m = baseMaterial(map, tx, ty);
  warpX = 0;
  warpY = 0;
  if (!WARPED.has(m)) return m;
  const wx = (fbm(x * 0.09, y * 0.09, 31, 2) - 0.5) * 9;
  const wy = (fbm(x * 0.09 + 40, y * 0.09, 32, 2) - 0.5) * 9;
  const w = baseMaterial(map, Math.floor((x + wx) / T), Math.floor((y + wy) / T));
  if (!WARPED.has(w)) return m;
  warpX = wx;
  warpY = wy;
  return w;
}

function grass(x: number, y: number, meadow: boolean): RGB {
  let c = mix(C.grassDark, C.grassLight, smoothstep(0.25, 0.75, fbm(x / 52, y / 52, 1)));
  if (meadow) c = mix(c, C.meadow, 0.25);
  const k = 0.9 + noise2(x * 0.8, y * 0.8, 2) * 0.2;
  c = [c[0] * k, c[1] * k, c[2] * k];
  const blade = hash2(Math.floor(x * 2), Math.floor(y * 0.7), 3);
  if (blade < 0.07) c = [c[0] * 0.8, c[1] * 0.83, c[2] * 0.8];
  else if (blade > 0.975) c = mix(c, C.grassSun, 0.6);
  const dapple = fbm(x / 22, y / 22, 4);
  if (dapple > 0.6) c = mix(c, C.grassSun, smoothstep(0.6, 0.7, dapple) * 0.25);
  // Worn patches where the grass gives way to earth, and darker clover clumps.
  const dirt = fbm(x / 46, y / 46, 921, 2);
  if (dirt > 0.7) c = mix(c, C.pathA, smoothstep(0.7, 0.8, dirt) * 0.55 * (0.8 + noise2(x * 0.5, y * 0.5, 922) * 0.4));
  const clump = noise2(x / 9, y / 9, 923);
  if (clump > 0.74) c = mix(c, C.grassDark, smoothstep(0.74, 0.82, clump) * 0.5);
  return c;
}

function path(map: MapDef, x: number, y: number): RGB {
  let c = mix(C.pathA, C.pathB, fbm(x / 30, y / 30, 5));
  const k = 0.94 + noise2(x * 1.3, y * 1.3, 6) * 0.12;
  c = [c[0] * k, c[1] * k, c[2] * k];
  // Mossy edges where the grass creeps in.
  let g = 0;
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  const green = (dx: number, dy: number) => {
    const ch = tileAt(map, tx + dx, ty + dy);
    return ch === 'g' || ch === 'G';
  };
  if (fx < 4 && green(-1, 0)) g++;
  if (fx > T - 4 && green(1, 0)) g++;
  if (fy < 4 && green(0, -1)) g++;
  if (fy > T - 4 && green(0, 1)) g++;
  if (g) c = mix(c, C.pathMoss, g * 0.09);
  // Pebbles on a jittered grid.
  const cx = Math.floor(x / 5), cy = Math.floor(y / 5);
  if (hash2(cx, cy, 7) < 0.3) {
    const px = cx * 5 + 1 + hash2(cx, cy, 8) * 3, py = cy * 5 + 1 + hash2(cx, cy, 9) * 3;
    const d = Math.hypot(x - px, (y - py) * 1.4);
    if (d < 1.1) c = y < py ? C.pebbleL : C.pebbleD;
  }
  return c;
}

function pavement(x: number, y: number): RGB {
  // The odd manhole cover, on a coarse grid so they are rare.
  const gx = Math.floor(x / 96), gy = Math.floor(y / 96);
  if (hash2(gx, gy, 931) < 0.35) {
    const mx = gx * 96 + 16 + hash2(gx, gy, 932) * 64, my = gy * 96 + 16 + hash2(gx, gy, 933) * 64;
    const d = Math.hypot(x - mx, y - my);
    if (d < 5.5) {
      if (d > 4.6) return hex('#5a5650');
      const ring = Math.abs(d - 2.8) < 0.5 || (d < 1.2);
      const base = mix(hex('#6d6862'), hex('#7d7872'), hash2(Math.floor(x * 2), Math.floor(y * 2), 934));
      return ring ? scale(base, 0.75) : base;
    }
  }
  const row = Math.floor(y / 8);
  const sx = x + (row % 2) * 4;
  const cx = Math.floor(sx / 8);
  const u = sx - cx * 8, v = y - row * 8;
  if (u < 0.6 || v < 0.6) return hash2(cx, row, 2) < 0.15 ? C.moss : C.grout;
  let c = mix(C.stoneA, C.stoneB, hash2(cx, row, 1));
  const k = 0.95 + noise2(x * 0.9, y * 0.9, 3) * 0.1;
  c = [c[0] * k, c[1] * k, c[2] * k];
  if (u < 1.6 || v < 1.6) c = [c[0] * 1.07, c[1] * 1.07, c[2] * 1.07];
  if (u > 6.9 || v > 6.9) c = [c[0] * 0.88, c[1] * 0.88, c[2] * 0.88];
  // Gum spots, stains and a cracked slab here and there.
  if (hash2(cx, row, 935) < 0.04) c = [c[0] * 0.84, c[1] * 0.84, c[2] * 0.86];
  const stain = fbm(x / 28, y / 28, 936, 2);
  if (stain > 0.74) c = mix(c, hex('#6f6a62'), smoothstep(0.74, 0.86, stain) * 0.35);
  return c;
}

function road(x: number, y: number): RGB {
  const k = 0.92 + noise2(x * 0.7, y * 0.7, 11) * 0.16;
  let c: RGB = [C.road[0] * k, C.road[1] * k, C.road[2] * k];
  if (hash2(Math.floor(x * 2), Math.floor(y * 2), 12) < 0.05) c = C.roadSpeck;
  // Oil stains and patched asphalt instead of crack lines.
  const patch = noise2(x / 30, y / 30, 13);
  if (patch > 0.78) c = [c[0] * 0.9, c[1] * 0.9, c[2] * 0.92];
  // Darker tyre tracks along the lanes.
  const lane = ((y % 32) + 32) % 32;
  if ((lane > 5 && lane < 9) || (lane > 22 && lane < 26)) c = [c[0] * 0.93, c[1] * 0.93, c[2] * 0.95];
  return c;
}

function waterDist(map: MapDef, x: number, y: number): number {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  const land = (dx: number, dy: number) => tileAt(map, tx + dx, ty + dy) !== '~';
  let d = 40;
  if (land(-1, 0)) d = Math.min(d, fx);
  if (land(1, 0)) d = Math.min(d, T - fx);
  if (land(0, -1)) d = Math.min(d, fy);
  if (land(0, 1)) d = Math.min(d, T - fy);
  if (land(-2, 0)) d = Math.min(d, fx + T);
  if (land(2, 0)) d = Math.min(d, 2 * T - fx);
  if (land(0, -2)) d = Math.min(d, fy + T);
  if (land(0, 2)) d = Math.min(d, 2 * T - fy);
  if (land(-1, -1)) d = Math.min(d, Math.hypot(fx, fy));
  if (land(1, -1)) d = Math.min(d, Math.hypot(T - fx, fy));
  if (land(-1, 1)) d = Math.min(d, Math.hypot(fx, T - fy));
  if (land(1, 1)) d = Math.min(d, Math.hypot(T - fx, T - fy));
  return d;
}

function water(map: MapDef, x: number, y: number): RGB {
  const d = waterDist(map, x, y) + (noise2(x * 0.15, y * 0.15, 21) - 0.5) * 1.5;
  if (d < 1.3) return C.wetSand;
  if (d < 2.2) return mix(C.foam, C.shallow, 0.2);
  let c = mix(C.shallow, C.deep, smoothstep(1.5, 26, d));
  // Soft light patches (sky reflection) rather than hard caustic lines.
  const sky = fbm(x / 26, y / 12, 22);
  c = mix(c, hex('#a8dcef'), smoothstep(0.58, 0.75, sky) * 0.35);
  return c;
}

function wood(x: number, y: number): RGB {
  const row = Math.floor(y / 5);
  const off = hash2(row, 0, 41) * 48;
  const plank = Math.floor((x + off) / 48);
  let c = C.woods[Math.floor(hash2(row, plank, 42) * C.woods.length)];
  const g = noise2((x + off) * 0.1, y * 1.5 + plank * 7, 43);
  const ring = Math.sin((x + off) * 0.35 + g * 7);
  const k = 0.9 + g * 0.16 + ring * 0.03;
  c = [c[0] * k, c[1] * k, c[2] * k];
  const v = y - row * 5;
  if (v < 0.5) c = [c[0] * 0.62, c[1] * 0.6, c[2] * 0.58];
  else if (v < 1.2) c = [c[0] * 1.08, c[1] * 1.08, c[2] * 1.08];
  if ((x + off) % 48 < 0.5) c = [c[0] * 0.7, c[1] * 0.68, c[2] * 0.66];
  return c;
}

function tiled(x: number, y: number, size: number, a: RGB, b: RGB, grout: RGB): RGB {
  const cx = Math.floor(x / size), cy = Math.floor(y / size);
  const u = x - cx * size, v = y - cy * size;
  if (u < 0.5 || v < 0.5) return grout;
  let c = (cx + cy) % 2 ? b : a;
  const k = 1 + (1 - v / size) * 0.04 + (hash2(cx, cy, 51) - 0.5) * 0.04;
  c = [c[0] * k, c[1] * k, c[2] * k];
  if (u < 1.5 && v < 1.5) c = [c[0] * 1.08, c[1] * 1.08, c[2] * 1.08];
  return c;
}

function carpet(map: MapDef, x: number, y: number, self: string, pal: RGB[]): RGB {
  const inside = (dx: number, dy: number) => tileAt(map, Math.floor((x + dx) / T), Math.floor((y + dy) / T)) === self;
  const edge = !inside(-3, 0) || !inside(3, 0) || !inside(0, -3) || !inside(0, 3);
  const edge2 = !inside(-5, 0) || !inside(5, 0) || !inside(0, -5) || !inside(0, 5);
  if (edge && !(!inside(-1.5, 0) || !inside(1.5, 0) || !inside(0, -1.5) || !inside(0, 1.5))) return pal[2];
  let c = pal[0];
  if (edge2) c = pal[1];
  const dmd = Math.abs((x % 12) - 6) + Math.abs((y % 12) - 6);
  if (!edge2 && dmd < 1.6) c = pal[2];
  else if (!edge2 && dmd < 2.6) c = pal[1];
  const weave = (Math.floor(x * 2) + Math.floor(y * 2)) % 2 ? 1.03 : 0.97;
  return [c[0] * weave, c[1] * weave, c[2] * weave];
}

/** Ambient occlusion: darken ground hugging walls, buildings and hedges. */
function occlusion(map: MapDef, x: number, y: number, mat: string): number {
  const blocks = (dx: number, dy: number) => {
    const ch = tileAt(map, Math.floor((x + dx) / T), Math.floor((y + dy) / T));
    return ch === '#' || ch === ' ' || BUILDING_WALLS.has(ch) || ROOFS.has(ch) || ch === 'h';
  };
  let k = 1;
  if (blocks(0, -3)) k *= 0.7;
  else if (blocks(0, -7)) k *= 0.85;
  if ('.,:;kbzmue'.includes(mat)) {
    if (blocks(-2, 0) || blocks(2, 0)) k *= 0.82;
    else if (blocks(-5, 0) || blocks(5, 0)) k *= 0.92;
  }
  return k;
}

/** Colour of a ground pixel, or null for the void beyond the map (left transparent). */
function colorAt(map: MapDef, x: number, y: number): RGB | null {
  const mat = materialAt(map, x, y);
  let c: RGB;
  switch (mat) {
    case 'g':
      c = grass(x, y, false);
      break;
    case 'G':
      c = grass(x, y, true);
      break;
    case 'F': {
      const band = Math.floor(x / 32) % 2;
      const edge = smoothstep(0, 2, Math.min(x % 32, 32 - (x % 32)));
      c = mix(mix(C.fieldA, C.fieldB, 0.5), band ? C.fieldB : C.fieldA, edge);
      const g = grass(x, y, false);
      c = mix(c, g, 0.25);
      break;
    }
    case 'p':
      c = path(map, x, y);
      break;
    case 'S': {
      c = mix(C.sandA, C.sandB, noise2(x * 0.3, y * 0.3, 61));
      if (hash2(Math.floor(x * 2), Math.floor(y * 2), 62) < 0.08) c = [c[0] * 0.88, c[1] * 0.86, c[2] * 0.82];
      break;
    }
    case 't': {
      c = mix(C.trackA, C.trackB, noise2(x * 0.5, y * 0.5, 71));
      if (hash2(Math.floor(x * 2), Math.floor(y * 2), 72) < 0.1) c = [c[0] * 0.9, c[1] * 0.9, c[2] * 0.9];
      break;
    }
    case 'r':
      c = road(x, y);
      break;
    case 's':
      c = pavement(x, y);
      break;
    case '~':
      // Distance to the shore measured in the same warped space as the material lookup.
      c = water(map, x + warpX, y + warpY);
      break;
    case 'c': {
      // Hard court: blue playing area inside a green apron.
      const tx = Math.floor(x / T), ty = Math.floor(y / T);
      const inner = tileAt(map, tx - 1, ty) === 'c' && tileAt(map, tx + 1, ty) === 'c' && tileAt(map, tx, ty - 1) === 'c' && tileAt(map, tx, ty + 1) === 'c';
      c = inner ? hex('#3f7fbf') : hex('#3f9a6a');
      const k = 0.96 + noise2(x * 0.6, y * 0.6, 81) * 0.08;
      c = [c[0] * k, c[1] * k, c[2] * k];
      break;
    }
    case 'P': {
      // Indoor pool: tiled bottom seen through water, lane stripes.
      const lane = Math.floor(y / 16) % 2 === 0;
      c = mix(hex('#5ec8e6'), hex('#2f9cd0'), noise2(x * 0.08, y * 0.12, 82));
      if (Math.floor(x) % 8 === 0 || Math.floor(y) % 8 === 0) c = mix(c, hex('#ffffff'), 0.12);
      if (lane && Math.abs((y % 16) - 8) < 0.6) c = mix(c, hex('#1f5f9e'), 0.6);
      break;
    }
    case '.':
      c = wood(x, y);
      break;
    case ',':
      c = tiled(x, y, 8, C.tileCream, C.tileSage, C.tileGrout);
      break;
    case 'k':
      c = tiled(x, y, 8, C.kitchenW, C.kitchenB, C.tileGrout);
      break;
    case 'b':
      c = tiled(x, y, 4, C.bathA, C.bathB, C.tileGrout);
      break;
    case ':':
      c = carpet(map, x, y, ':', C.carpetRed);
      break;
    case ';':
      c = carpet(map, x, y, ';', C.carpetGreen);
      break;
    default: {
      const ext = colorAt2(map, x, y, mat);
      if (!ext) return null;
      c = ext;
    }
  }
  // Wear: slow, large-scale dirt and sun-bleaching so hard surfaces never read as flat fills.
  if (!WARPED.has(mat) && mat !== 'q' && mat !== 'P' && mat !== 'j') {
    const w = 0.89 + 0.13 * fbm(x / 64, y / 64, 909, 2);
    c = [c[0] * w, c[1] * w, c[2] * w];
  }
  const k = occlusion(map, x, y, mat);
  return k === 1 ? c : [c[0] * k, c[1] * k, c[2] * k];
}

/** Generate the RGBA pixels of one ground chunk (pure: runs in a worker). */
export function renderPixels(map: MapDef, cx: number, cy: number): Uint8ClampedArray {
  const size = CHUNK * D;
  const data = new Uint8ClampedArray(size * size * 4);
  const ox = cx * CHUNK, oy = cy * CHUNK;
  const step = 1 / D;
  for (let py = 0; py < size; py++) {
    const y = oy + py * step + step / 2;
    for (let px = 0; px < size; px++) {
      const x = ox + px * step + step / 2;
      const col = colorAt(map, x, y);
      if (!col) continue;
      const i = (py * size + px) * 4;
      data[i] = col[0];
      data[i + 1] = col[1];
      data[i + 2] = col[2];
      data[i + 3] = 255;
    }
  }
  return data;
}

/** Cheap flat colour per tile, used while the detailed chunk is being generated (null = void). */
export function placeholderColor(map: MapDef, tx: number, ty: number): string | null {
  const ch = tileAt(map, tx, ty);
  if (ch === ' ') return null;
  const m: Record<string, string> = {
    g: '#64a854', G: '#6cad58', F: '#62ad55', p: '#d4bc8c', s: '#cbc5b9', r: '#4a4b51', l: '#4a4b51', '=': '#4a4b51',
    '~': '#4c93c6', S: '#ecd59c', t: '#c25f42', h: '#2f7a3a', f: '#64a854', '.': '#a46d3e', ',': '#dcd8c6', ':': '#b85a44',
    ';': '#44805f', k: '#cfe0e6', b: '#e6f2f5', d: '#a46d3e', D: '#7a9e5c', c: '#3f7fbf', P: '#3fa8d8',
  };
  return m[ch] ?? PLACEHOLDER2[ch] ?? PLACEHOLDER3[ch] ?? null;
}
